// Engine-agnostic operator lattices for non-JSON operand classifications.
// PRD: Ralph/JsonColumnSemantics/PRD.md § Slice C2 (acceptance bullet 2/3).
//
// Five lattices:
// - NUMERIC_OPERATORS: integer / numeric literals — full comparison + BETWEEN +
//   IN/NOT IN + IS [NOT] NULL.
// - TEXT_OPERATORS: text literals — equality + LIKE + IN/NOT IN + IS [NOT] NULL.
// - BOOLEAN_OPERATORS: boolean literals — equality + IS [NOT] NULL.
// - NULL_OPERATORS: NULL literal — IS [NOT] NULL.
// - GENERIC_COLUMN_OPERATORS: fallback when an operand's column type is unknown
//   or `kind: 'expression'` without `resultClassification`. Conservative union
//   of comparison + IN/NOT IN + IS [NOT] NULL. Engine-agnostic — engines that
//   want a tighter list per column type can fold via column metadata in a
//   future slice.
//
// `Object.freeze` on the arrays guards consumers from accidental mutation —
// the returned shape is `readonly Operator[]` but JS arrays are mutable at
// runtime, so the freeze + cached singleton matches A1's NOT_JSON / A2's
// NATIVE_JSON cached-constant pattern (allocation-free hot path).

import type { Operator } from './types';

export const NUMERIC_OPERATORS: readonly Operator[] = Object.freeze([
  { token: '=', label: 'equals', snippetTemplate: '= ${1:value}' },
  { token: '<>', label: 'not equals', snippetTemplate: '<> ${1:value}' },
  { token: '<', label: 'less than', snippetTemplate: '< ${1:value}' },
  { token: '>', label: 'greater than', snippetTemplate: '> ${1:value}' },
  { token: '<=', label: 'less than or equal', snippetTemplate: '<= ${1:value}' },
  { token: '>=', label: 'greater than or equal', snippetTemplate: '>= ${1:value}' },
  { token: 'IS NULL', label: 'is null', snippetTemplate: 'IS NULL' },
  { token: 'IS NOT NULL', label: 'is not null', snippetTemplate: 'IS NOT NULL' },
  { token: 'BETWEEN', label: 'between', snippetTemplate: 'BETWEEN ${1:low} AND ${2:high}' },
  { token: 'IN', label: 'in list', snippetTemplate: 'IN (${1:values})' },
  { token: 'NOT IN', label: 'not in list', snippetTemplate: 'NOT IN (${1:values})' },
]);

export const TEXT_OPERATORS: readonly Operator[] = Object.freeze([
  { token: '=', label: 'equals', snippetTemplate: "= '${1:value}'" },
  { token: '<>', label: 'not equals', snippetTemplate: "<> '${1:value}'" },
  { token: 'LIKE', label: 'matches pattern', snippetTemplate: "LIKE '${1:%pattern%}'" },
  { token: 'IS NULL', label: 'is null', snippetTemplate: 'IS NULL' },
  { token: 'IS NOT NULL', label: 'is not null', snippetTemplate: 'IS NOT NULL' },
  { token: 'IN', label: 'in list', snippetTemplate: 'IN (${1:values})' },
  { token: 'NOT IN', label: 'not in list', snippetTemplate: 'NOT IN (${1:values})' },
]);

export const BOOLEAN_OPERATORS: readonly Operator[] = Object.freeze([
  { token: '=', label: 'equals', snippetTemplate: '= ${1:true}' },
  { token: '<>', label: 'not equals', snippetTemplate: '<> ${1:true}' },
  { token: 'IS NULL', label: 'is null', snippetTemplate: 'IS NULL' },
  { token: 'IS NOT NULL', label: 'is not null', snippetTemplate: 'IS NOT NULL' },
]);

export const NULL_OPERATORS: readonly Operator[] = Object.freeze([
  { token: 'IS NULL', label: 'is null', snippetTemplate: 'IS NULL' },
  { token: 'IS NOT NULL', label: 'is not null', snippetTemplate: 'IS NOT NULL' },
]);

export const GENERIC_COLUMN_OPERATORS: readonly Operator[] = Object.freeze([
  { token: '=', label: 'equals', snippetTemplate: '= ${1:value}' },
  { token: '<>', label: 'not equals', snippetTemplate: '<> ${1:value}' },
  { token: '<', label: 'less than', snippetTemplate: '< ${1:value}' },
  { token: '>', label: 'greater than', snippetTemplate: '> ${1:value}' },
  { token: '<=', label: 'less than or equal', snippetTemplate: '<= ${1:value}' },
  { token: '>=', label: 'greater than or equal', snippetTemplate: '>= ${1:value}' },
  { token: 'IS NULL', label: 'is null', snippetTemplate: 'IS NULL' },
  { token: 'IS NOT NULL', label: 'is not null', snippetTemplate: 'IS NOT NULL' },
  { token: 'IN', label: 'in list', snippetTemplate: 'IN (${1:values})' },
  { token: 'NOT IN', label: 'not in list', snippetTemplate: 'NOT IN (${1:values})' },
]);
